import { useState, useEffect } from 'react';
import {
  Trash2,
  ArrowRightLeft,
  ChevronDown,
  ChevronUp,
  Save,
  Loader2,
  BarChart3,
  Clock,
  Sparkles,
  User,
} from 'lucide-react';
import type { Tutee } from '../../../types/tuition';
import {
  updateFlashcard,
  type Flashcard,
  type FlashcardDeck,
  type FlashcardProgress,
  type CardReviewSummary,
} from '../../../services/flashcardService';
import type { CardStatus, Rating } from '../../../lib/spacedRepetition/sm2';
import { formatFlashcardLastSeen } from '../../../utils/flashcardInsights';

/** SM-2 phase — not the same as “not studied” (that’s the Not studied chip). */
const STATUS_META: Record<
  CardStatus | 'new',
  { label: string; className: string; title: string }
> = {
  new: {
    label: 'New',
    className: 'bg-slate-100 text-slate-600',
    title: 'Never reviewed',
  },
  learning: {
    label: 'Learning',
    className: 'bg-amber-50 text-amber-800',
    title: 'Reviewed at least once; still on short learning steps (not on long-term schedule yet)',
  },
  review: {
    label: 'Review',
    className: 'bg-emerald-50 text-emerald-800',
    title: 'On the long-interval review schedule',
  },
  relearning: {
    label: 'Relearning',
    className: 'bg-orange-50 text-orange-800',
    title: 'Forgotten after a lapse; back in learning steps',
  },
};

const RATING_CHIPS: Record<Rating, { label: string; className: string }> = {
  again: { label: 'Again', className: 'bg-red-100 text-red-800' },
  hard: { label: 'Hard', className: 'bg-orange-100 text-orange-800' },
  good: { label: 'Good', className: 'bg-green-100 text-green-800' },
  easy: { label: 'Easy', className: 'bg-blue-100 text-blue-800' },
};

interface FlashcardAdminCardItemProps {
  card: Flashcard;
  decks: FlashcardDeck[];
  progress: FlashcardProgress | null;
  summary: CardReviewSummary | undefined;
  selectedTutee?: Tutee;
  moving: boolean;
  onMove: (deckId: string | null) => void;
  onDelete: () => void;
  onUpdated: () => void;
}

const FlashcardAdminCardItem = ({
  card,
  decks,
  progress,
  summary,
  selectedTutee,
  moving,
  onMove,
  onDelete,
  onUpdated,
}: FlashcardAdminCardItemProps) => {
  const [draftFront, setDraftFront] = useState(card.front);
  const [draftBack, setDraftBack] = useState(card.back);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setDraftFront(card.front);
    setDraftBack(card.back);
    setSaveError('');
  }, [card.id, card.front, card.back]);

  const isDirty = draftFront.trim() !== card.front || draftBack.trim() !== card.back;

  const reviewCount = summary?.reviewCount ?? 0;
  const isUnstudied = reviewCount === 0 && !progress;
  const status: CardStatus | 'new' = progress?.status ?? 'new';
  const statusMeta = STATUS_META[status];
  const lastSeenLabel =
    summary?.lastSeenLabel ??
    (progress?.lastReviewedAt ? formatFlashcardLastSeen(progress.lastReviewedAt) : null);

  const ratingCounts = summary?.ratingCounts ?? {
    again: 0,
    hard: 0,
    good: 0,
    easy: 0,
  };

  const studentLabel = card.studentName
    ? card.studentName.charAt(0).toUpperCase() + card.studentName.slice(1)
    : null;

  const handleSave = async () => {
    const front = draftFront.trim();
    const back = draftBack.trim();
    if (!front || !back) {
      setSaveError('Front and back cannot be empty.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await updateFlashcard(card.id, { front, back });
      onUpdated();
    } catch (err) {
      console.error(err);
      setSaveError('Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="p-3 sm:p-4 bg-white border border-gray-100 rounded-xl shadow-sm space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block min-w-0">
          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md">
            Front
          </span>
          <textarea
            value={draftFront}
            onChange={(e) => setDraftFront(e.target.value)}
            rows={3}
            className="mt-1.5 w-full text-sm font-medium text-gray-900 border border-indigo-100 rounded-xl px-3 py-2 resize-y min-h-[4.5rem] focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-300 outline-none"
            placeholder="Question / prompt"
          />
        </label>
        <label className="block min-w-0">
          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-md">
            Back
          </span>
          <textarea
            value={draftBack}
            onChange={(e) => setDraftBack(e.target.value)}
            rows={3}
            className="mt-1.5 w-full text-sm text-gray-700 border border-emerald-100 rounded-xl px-3 py-2 resize-y min-h-[4.5rem] focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-300 outline-none"
            placeholder="Answer"
          />
        </label>
      </div>

      {saveError && <p className="text-xs text-red-600 font-medium">{saveError}</p>}

      <div className="flex flex-col gap-3 pt-3 border-t border-gray-100 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {studentLabel && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-sky-50 text-sky-800 text-xs font-bold"
              title="Added by student"
            >
              <User className="w-3.5 h-3.5" />
              {studentLabel}
            </span>
          )}
          {isUnstudied ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-bold"
              title="No reviews logged for this card yet"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              Not studied
            </span>
          ) : (
            <>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-violet-50 text-violet-800 text-xs font-bold">
                <BarChart3 className="w-3.5 h-3.5" />
                Seen {reviewCount}×
              </span>
              {(progress?.lapses ?? 0) > 0 && (
                <span className="px-2 py-1 rounded-lg bg-red-50 text-red-700 text-xs font-bold">
                  {progress!.lapses} lapse{progress!.lapses !== 1 ? 's' : ''}
                </span>
              )}
              <span
                className={`px-2 py-1 rounded-lg text-xs font-bold ${statusMeta.className}`}
                title={statusMeta.title}
              >
                {statusMeta.label}
              </span>
              {lastSeenLabel && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-50 text-gray-600 text-xs font-medium">
                  <Clock className="w-3 h-3" />
                  {lastSeenLabel}
                </span>
              )}
              {(['again', 'hard', 'good', 'easy'] as Rating[])
                .filter((r) => ratingCounts[r] > 0)
                .map((r) => {
                  const chip = RATING_CHIPS[r];
                  return (
                    <span
                      key={r}
                      className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wide ${chip.className}`}
                    >
                      {chip.label} {ratingCounts[r]}×
                    </span>
                  );
                })}
            </>
          )}
          {!isUnstudied && progress && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold text-indigo-700 hover:bg-indigo-50 transition-colors"
              aria-expanded={expanded}
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {expanded ? 'Hide SRS' : 'SRS'}
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2 sm:justify-end">
          {isDirty && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 ${
                selectedTutee
                  ? `bg-gradient-to-r ${selectedTutee.colorScheme.gradient}`
                  : 'bg-indigo-600'
              }`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </button>
          )}
          <label className="flex flex-col gap-1 min-w-[10rem] flex-1 sm:flex-initial sm:max-w-[12rem]">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide flex items-center gap-1">
              <ArrowRightLeft className="w-3 h-3" />
              Move to
            </span>
            <select
              value={card.deckId ?? ''}
              disabled={moving}
              onChange={(e) => onMove(e.target.value ? e.target.value : null)}
              className="w-full text-xs font-medium border border-gray-200 rounded-lg px-2 py-2 bg-gray-50 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 outline-none disabled:opacity-50"
            >
              <option value="">Unassigned</option>
              {decks.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={onDelete}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
            title="Delete card"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {expanded && progress && (
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs animate-fade-in">
          {[
            { label: 'Ease', value: progress.easeFactor?.toFixed(2) ?? '—' },
            {
              label: 'Interval',
              value: progress.intervalDays >= 1 ? `${Math.round(progress.intervalDays)}d` : '<1d',
            },
            { label: 'Reps', value: String(progress.reps) },
            {
              label: 'Due',
              value: new Date(progress.dueAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              }),
            },
            ...(progress.lastRating
              ? [
                  {
                    label: 'Last rating',
                    value: progress.lastRating.charAt(0).toUpperCase() + progress.lastRating.slice(1),
                  },
                ]
              : []),
          ].map(({ label, value }) => (
            <div key={label} className="px-2 py-1.5 rounded-lg bg-gray-50 border border-gray-100">
              <dt className="font-bold text-gray-400 uppercase tracking-wide text-[10px]">{label}</dt>
              <dd className="font-semibold text-gray-800 mt-0.5">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
};

export default FlashcardAdminCardItem;
